'use client'

import { Suspense, useEffect, useRef } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { toast } from 'sonner'

/**
 * Friendly copy for the `reason` codes a CRM OAuth callback can append on failure.
 * Mirrors the `reason` values emitted by `app/api/oauth/<provider>/callback`.
 */
const REASON_MESSAGES: Record<string, string> = {
  state_mismatch: 'Security check failed. Please start the connection again.',
  token_exchange_failed: 'Could not complete authorization with the provider.',
  persist_failed: 'Authorization succeeded but saving the connection failed.',
  integration_not_found: 'The integration could not be found.',
  wrong_provider: 'Provider mismatch during authorization.',
  missing_subdomain: 'The account subdomain was missing.',
  subdomain_mismatch: 'The authorized account did not match the subdomain you entered.',
  bad_request: 'The authorization request was invalid.',
}

function OAuthReturnToastInner() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const handled = useRef(false)

  useEffect(() => {
    // The CRM OAuth callback lands the user back here with `?<provider>_oauth=success|error`
    // (+ `&reason=` on error). Scan generically so any provider's callback works without changes.
    let oauthKey: string | null = null
    let status: string | null = null
    for (const [key, value] of searchParams.entries()) {
      if (key.endsWith('_oauth')) {
        oauthKey = key
        status = value
        break
      }
    }
    if (!oauthKey || handled.current) return
    handled.current = true

    const provider = oauthKey.replace(/_oauth$/, '')
    const label = provider.charAt(0).toUpperCase() + provider.slice(1)

    if (status === 'success') {
      toast.success(`${label} connected successfully.`)
    } else {
      const reason = searchParams.get('reason')
      const detail = (reason && REASON_MESSAGES[reason]) || 'Connection could not be completed.'
      toast.error(`${label} connection failed`, { description: detail })
    }

    // Strip only the OAuth-return params, preserving any others on the surface.
    const next = new URLSearchParams(searchParams.toString())
    next.delete(oauthKey)
    next.delete('reason')
    const qs = next.toString()
    router.replace(qs ? `${pathname}?${qs}` : pathname)
  }, [searchParams, router, pathname])

  return null
}

/**
 * Global, surface-agnostic handler for CRM OAuth returns. Mounted once in the app shell: it toasts
 * the success/failure of an OAuth install and removes the `?<provider>_oauth=…` params from the URL.
 * Integration lists refetch on the full-page reload that the OAuth round-trip already triggers, so
 * no explicit refresh is needed here.
 */
export function OAuthReturnToast() {
  return (
    <Suspense fallback={null}>
      <OAuthReturnToastInner />
    </Suspense>
  )
}
